// Leads — v1.1.0 (Lead Management + Lease-Leverage Module)
//
// Pipeline tracker for prospective deals. Themed to the dark Layout shell
// so the sidebar/page transition is seamless.
//
// Status flow:  new → analyzing → under_review → active | pass
//
// Pre-fill handoff: "Run Analysis" navigates to /deal-analyzer with the
// lead's address / lat / lng / zip / city / state stuffed onto location
// state — DealAnalyzer's buildInitialForm() reads the same shape that
// MyAnalyses passes, so no changes are needed there.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createLead, listLeads, updateLead, deleteLead } from "../lib/leads";
import {
  leadCreated,
  leadStatusChanged,
  leadDeleted,
  leadToDealAnalyzer,
} from "../lib/leadsAnalytics";
import { listLeadAnalyses } from "../lib/analyses";
import { createLeaseTerms } from "../lib/leaseTerms";
import { API_BASE_URL } from "../services/api";
import AddressAutocomplete from "../components/AddressAutocomplete";

// =========================
// DATA — entry types, statuses, lease defaults
// =========================
const ENTRY_TYPE_OPTIONS = [
  { value: "lease_leverage", label: "Lease-Leverage" },
  { value: "fsbo",           label: "FSBO" },
  { value: "parcel",         label: "Parcel" },
  { value: "off_market",     label: "Off-Market" },
  { value: "other",          label: "Other" },
];
const ENTRY_TYPE_LABEL = Object.fromEntries(
  ENTRY_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

const STATUS_OPTIONS = [
  { value: "new",          label: "New" },
  { value: "analyzing",    label: "Analyzing" },
  { value: "under_review", label: "Under review" },
  { value: "active",       label: "Active" },
  { value: "pass",         label: "Pass" },
];
const STATUS_LABEL = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, o.label])
);

// Map a lead status to its design system badge class.
const STATUS_BADGE_CLASS = {
  new:          "badge-new",
  analyzing:    "badge-analyzing",
  under_review: "badge-reviewing",
  active:       "badge-active",
  pass:         "badge-pass",
};

// Map a recommendation decision (BUY / HOLD / PASS / LEASE) to its design
// system badge class. Mirrors the helper in DealAnalyzer.js — kept in sync
// so the Saved Analyses section on the lead card matches the analyzer's
// headline badge styling. Returns empty string for unknown / null values.
function recommendationBadgeClass(decision) {
  const d = String(decision || "").toUpperCase();
  if (d === "BUY")   return "badge-buy";
  if (d === "HOLD")  return "badge-hold";
  if (d === "PASS")  return "badge-pass-rec";
  if (d === "LEASE") return "badge-lease";
  return "";
}

// Lease structure dropdown options — values must match the CHECK
// constraints on public.lease_terms (migration 022).
const ESCALATION_INDEX_OPTIONS = [
  { value: "cpi_u_lesser_of", label: "Lesser of fixed cap OR CPI-U" },
  { value: "fixed",           label: "Fixed cap" },
  { value: "none",            label: "None" },
];
const UTILITIES_PAID_BY_OPTIONS = [
  { value: "tenant",   label: "Tenant" },
  { value: "landlord", label: "Landlord" },
  { value: "split",    label: "Split" },
];
const IMPROVEMENT_VALUATION_OPTIONS = [
  { value: "greater_of_fmv_or_book", label: "Greater of FMV or book value" },
  { value: "fmv_only",               label: "FMV only" },
  { value: "book_only",              label: "Book value only" },
];

// Forgotten Coast deal defaults — used to seed the lease structure
// form so the canonical first deal is one click away. Override anything
// in the modal for other deals.
const LEASE_DEFAULTS = {
  base_rent_monthly:                   "1800",
  lease_term_months:                   "60",
  renewal_options_count:               "2",
  renewal_option_length_months:        "36",
  renewal_notice_days:                 "90",
  escalation_pct_cap:                  "2.0",
  escalation_index:                    "cpi_u_lesser_of",
  late_charge_pct:                     "5.0",
  grace_period_days:                   "5",
  sublet_allowed:                      true,
  utilities_paid_by:                   "tenant",
  option_window_start_month:           "24",
  option_window_end_month:             "60",
  option_price_appraised_at_effective: true,
  option_price_fixed:                  "",
  rofr_match_days:                     "10",
  improvement_valuation_method:        "greater_of_fmv_or_book",
  early_term_noi_multiplier_months:    "12",
  monthly_expense_utilities:           "250",
  monthly_expense_maintenance:         "150",
  monthly_expense_vacancy_reserve:     "200",
  monthly_expense_insurance:           "100",
};

// =========================
// FORMATTERS
// =========================
function usd(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return `$${Math.round(Number(n)).toLocaleString()}`;
}
function shortDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// =========================
// SECTION LABEL — small caps eyebrow
// =========================
function SectionLabel({ children, style }) {
  return (
    <div
      style={{
        fontSize: "var(--text-xs)",
        fontWeight: "var(--weight-bold)",
        letterSpacing: "var(--tracking-label)",
        textTransform: "uppercase",
        color: "var(--color-muted)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// =========================
// MAIN PAGE
// =========================
export default function Leads() {
  const navigate = useNavigate();
  const location = useLocation();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null); // null | lead row being edited
  const [busyId, setBusyId] = useState(null); // shared "row is being acted on" flag
  // DEF-015 — per-lead saved-analyses, keyed by lead_id. Batch-fetched
  // in `load` after rows resolve so each LeadCard renders its history
  // inline without N round trips.
  const [analysesByLead, setAnalysesByLead] = useState({});

  // If we landed here from a child page (e.g. /leads/:id/scenarios bouncing
  // back when no lease_terms exists), it may have stashed an errorMessage
  // in location.state. Surface it in the error banner once, then clear
  // history state so a refresh doesn't re-show it.
  useEffect(() => {
    const msg = location.state?.errorMessage;
    if (msg) {
      setError(msg);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const { data, error: listError } = await listLeads();
    if (listError) {
      setError(listError.message || "Could not load your leads.");
      setRows([]);
      setAnalysesByLead({});
      setLoading(false);
      return;
    }
    const nextRows = Array.isArray(data?.leads) ? data.leads : [];
    setRows(nextRows);

    // DEF-015 — batch-fetch lead_analyses for every lead in one round trip,
    // then bucket by lead_id for O(1) prop lookup. Failure is non-fatal:
    // the page still renders, lead cards just show "No analyses saved yet".
    const leadIds = nextRows.map((r) => r.id);
    const { data: analysesRows, error: analysesError } = await listLeadAnalyses(leadIds);
    if (analysesError) {
      // eslint-disable-next-line no-console
      console.warn('[Leads] could not load saved analyses:', analysesError.message);
      setAnalysesByLead({});
    } else {
      const grouped = {};
      for (const row of analysesRows || []) {
        if (!grouped[row.lead_id]) grouped[row.lead_id] = [];
        grouped[row.lead_id].push(row);
      }
      setAnalysesByLead(grouped);
    }

    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // The modal hands us a two-part payload:
  //   { lead: <required fields>, lease_terms: <fields | null> }
  //
  // We run a two-step transaction: create the lead, then if a lease_terms
  // payload was supplied, create that too. If lease_terms fails after the
  // lead was created, we roll back by deleting the lead — otherwise the UI
  // would have an orphan lead with no lease_terms and no way (yet) to
  // recover from the modal. The rollback is best-effort; if it itself
  // fails we surface a combined error.
  async function handleCreate(payload) {
    const { lead: leadInput, lease_terms: leaseTermsInput } = payload || {};

    const { data, error: createError } = await createLead(leadInput);
    if (createError) {
      return { error: createError };
    }
    const newLead = data.lead;

    if (leaseTermsInput) {
      const { error: leaseError } = await createLeaseTerms({
        lead_id: newLead.id,
        ...leaseTermsInput,
      });
      if (leaseError) {
        // Best-effort rollback so we don't leave an orphan lead.
        const { error: rollbackError } = await deleteLead(newLead.id);
        const detail = rollbackError
          ? ` (rollback also failed: ${rollbackError.message} — please delete the orphan lead manually from /leads)`
          : "";
        return {
          error: {
            message: `Lead created but lease structure couldn't be saved: ${leaseError.message}.${detail}`,
          },
        };
      }
    }

    // Insert at the top — get-leads orders newest first.
    setRows((prev) => [newLead, ...prev]);
    // Item 8 — product telemetry (PII-safe: entry enum only). track() env-tags
    // via BL-025 and no-ops when PostHog is unconfigured.
    leadCreated({ entry_type: newLead.entry_type });
    return { error: null };
  }

  // DEF-005 — edit handler. Patches the 4 server-supported fields via
  // updateLead and reconciles the row in local state. Returns the same
  // { error } shape onCreate uses so the modal can surface failures.
  async function handleUpdate(patch) {
    const { data, error: updateError } = await updateLead(patch);
    if (updateError) {
      return { error: updateError };
    }
    setRows((prev) => prev.map((r) => (r.id === patch.id ? data.lead : r)));
    return { error: null };
  }

  async function handleStatusChange(row, nextStatus) {
    if (nextStatus === row.status) return;
    setBusyId(row.id);
    const { data, error: patchError } = await updateLead({ id: row.id, status: nextStatus });
    setBusyId(null);
    if (patchError) {
      setError(patchError.message || "Could not update the lead status.");
      return;
    }
    setRows((prev) => prev.map((r) => (r.id === row.id ? data.lead : r)));
    // Item 8 — the pipeline funnel signal (M-005). Enums only.
    leadStatusChanged({ from_status: row.status, to_status: nextStatus });
  }

  async function handleAnalyze(row) {
    // DEF-007 — no guard. The previous `!address && !lead_name` early return
    // silently dropped navigation for pre-DEF-002 leads (lead_name was null
    // before that migration) and for parcel/off-market leads with only
    // lat/lng. DealAnalyzer's own form validation handles missing lat/lng
    // by disabling the Analyze Property button with an inline hint — empty
    // prefill is safe to navigate through.
    //
    // DealAnalyzer.buildInitialForm reads these keys off location.state.prefill.
    // We don't have a saved analysis result blob — DealAnalyzer falls back to
    // its defaults for financials, and only the address/coords are seeded.
    //
    // entry_type + lead_id (v1.0.0 Phase 3) let DealAnalyzer fetch STR-scenario
    // NOI for the lease-leverage overlay; both are no-ops for non-lease-leverage
    // entries since the engine only branches on entry_type === 'lease_leverage'.
    const prefill = {
      lat:                     row.lat,
      lng:                     row.lng,
      formatted_address:       row.address || "",
      street:                  "",
      city:                    row.city  || "",
      state:                   row.state || "",
      zip:                     row.zip   || "",
      place_id:                "",
      entry_type:              row.entry_type,
      lead_id:                 row.id,
      lead_name:               row.lead_name || "",
      asking_price:            row.asking_price ?? null,
      current_monthly_revenue: row.current_monthly_revenue ?? null,
    };

    // DEF-013 — leads created from parcel/off-market sources (or any path
    // where Google Places autocomplete didn't fire) often carry zip but
    // null lat/lng. Without coordinates DealAnalyzer disables the Analyze
    // Property button (DEF-007 hint), forcing the user to interact with
    // the ZIP override field before they can run analysis. Pre-geocode
    // the ZIP centroid here via the same forwardGeocode mode that
    // DealAnalyzer's ZIP override uses (DEF-009) so the user lands ready
    // to analyze. On geocode failure, navigate anyway — DealAnalyzer's
    // empty-lat/lng UX is the safety net.
    if ((!prefill.lat || !prefill.lng) && prefill.zip) {
      setBusyId(row.id);
      try {
        const res = await fetch(
          `${API_BASE_URL}/geocode-place?address=${encodeURIComponent(prefill.zip + " USA")}`
        );
        const data = await res.json();
        if (data.success) {
          prefill.lat = data.lat;
          prefill.lng = data.lng;
          if (!prefill.formatted_address && data.formattedAddress) {
            prefill.formatted_address = data.formattedAddress;
          }
          if (!prefill.city && data.components?.locality) {
            prefill.city = data.components.locality;
          }
          if (!prefill.state && data.components?.administrative_area_level_1_short) {
            prefill.state = data.components.administrative_area_level_1_short;
          }
        }
      } catch (e) {
        // geocode failed — navigate anyway, DealAnalyzer handles empty lat/lng
      } finally {
        setBusyId(null);
      }
    }

    // Item 8 — pipeline→analysis handoff (status enum only).
    leadToDealAnalyzer({ status: row.status });
    navigate("/deal-analyzer", { state: { prefill } });
  }

  async function handleDelete(row) {
    const label = row.lead_name || row.address || "this lead";
    const ok = window.confirm(`Delete ${label}? This can't be undone.`);
    if (!ok) return;
    setBusyId(row.id);
    const { error: deleteError } = await deleteLead(row.id);
    setBusyId(null);
    if (deleteError) {
      setError(deleteError.message || "Could not delete that lead.");
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    // Item 8 — deletion outcome (status enum at deletion).
    leadDeleted({ status: row.status });
  }

  return (
    <div
      style={{
        background: "var(--color-navy)",
        minHeight: "100%",
        padding: "var(--space-6)",
        color: "var(--color-white)",
        fontFamily: "var(--font-body)",
      }}
    >
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        {/* ── Page header ───────────────────────────────────── */}
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "var(--space-4)",
            marginBottom: "var(--space-6)",
            flexWrap: "wrap",
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "var(--text-4xl)",
                fontWeight: "var(--weight-black)",
                letterSpacing: "var(--tracking-tight)",
                textTransform: "uppercase",
                color: "var(--color-white)",
                margin: 0,
                marginBottom: "var(--space-2)",
              }}
            >
              Leads
            </h1>
            <p
              style={{
                color: "var(--color-steel-lt)",
                fontSize: "var(--text-md)",
                margin: 0,
              }}
            >
              {loading
                ? "Loading your leads…"
                : rows.length === 0
                ? "No leads in the pipeline yet."
                : `${rows.length} ${rows.length === 1 ? "lead" : "leads"} in your pipeline, newest first.`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="btn btn-primary"
          >
            + New Lead
          </button>
        </header>

        {/* ── Error banner ──────────────────────────────────── */}
        {error && (
          <div
            role="alert"
            style={{
              marginBottom: "var(--space-5)",
              padding: "var(--space-4) var(--space-5)",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--color-danger)",
              background: "var(--color-danger-ghost)",
              color: "var(--color-danger-lt)",
              fontWeight: "var(--weight-semibold)",
              fontSize: "var(--text-base)",
            }}
          >
            {error}
          </div>
        )}

        {/* ── Empty state ───────────────────────────────────── */}
        {!loading && rows.length === 0 && !error && (
          <EmptyState onNew={() => setShowModal(true)} />
        )}

        {/* ── Lead cards ────────────────────────────────────── */}
        <div style={{ display: "grid", gap: "var(--space-5)" }}>
          {rows.map((row) => (
            <LeadCard
              key={row.id}
              row={row}
              busy={busyId === row.id}
              savedAnalyses={analysesByLead[row.id] || []}
              onStatusChange={(s) => handleStatusChange(row, s)}
              onAnalyze={() => handleAnalyze(row)}
              onEdit={() => setEditingRow(row)}
              onScenarios={() => navigate(`/leads/${row.id}/scenarios`)}
              onImprovements={() => navigate(`/leads/${row.id}/improvements`)}
              onDelete={() => handleDelete(row)}
            />
          ))}
        </div>
      </div>

      {showModal && (
        <NewLeadModal
          onClose={() => setShowModal(false)}
          onCreate={handleCreate}
        />
      )}

      {editingRow && (
        <NewLeadModal
          mode="edit"
          initialValues={editingRow}
          onClose={() => setEditingRow(null)}
          onUpdate={handleUpdate}
        />
      )}
    </div>
  );
}

// =========================
// LEAD CARD
// =========================
export function LeadCard({ row, busy, savedAnalyses = [], onStatusChange, onAnalyze, onEdit, onScenarios, onImprovements, onDelete }) {
  const leadName = (row.lead_name || "").trim();
  // Test contract (LeadName.test.jsx): when lead_name is set, the address
  // string must NOT appear anywhere visible on the card. Title takes priority;
  // city/state are shown as a separate subtitle, never the raw address.
  const title = leadName || row.address || "Unspecified address";
  const cityState = [row.city, row.state].filter(Boolean).join(", ");
  const latLng =
    row.lat != null && row.lng != null
      ? `${Number(row.lat).toFixed(4)}, ${Number(row.lng).toFixed(4)}`
      : null;
  const notesPreview = (row.notes || "").trim().slice(0, 220);
  const statusBadgeClass = STATUS_BADGE_CLASS[row.status] || "";

  return (
    <article className="card">
      {/* ── Card header — title, meta, entry chip, status badge ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "var(--space-4)",
          flexWrap: "wrap",
          marginBottom: "var(--space-4)",
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "var(--text-2xl)",
              fontWeight: "var(--weight-bold)",
              color: "var(--color-white)",
              marginBottom: "var(--space-1)",
              wordBreak: "break-word",
              lineHeight: "var(--leading-tight)",
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--color-steel)",
              display: "flex",
              flexWrap: "wrap",
              gap: "var(--space-2)",
            }}
          >
            {cityState && <span>{cityState}</span>}
            {cityState && <span aria-hidden="true">·</span>}
            <span>Added {shortDate(row.created_at)}</span>
            {latLng && <span aria-hidden="true">·</span>}
            {latLng && <span>{latLng}</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
          <span
            className="badge"
            style={{
              background: "var(--color-navy-lt)",
              color: "var(--color-steel-lt)",
              border: "1px solid var(--color-navy-border)",
            }}
          >
            {ENTRY_TYPE_LABEL[row.entry_type] || row.entry_type}
          </span>
          <span className={`badge ${statusBadgeClass}`}>
            {STATUS_LABEL[row.status] || row.status}
          </span>
        </div>
      </div>

      {/* ── Metrics grid ─────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: "var(--space-3)",
          marginBottom: notesPreview ? "var(--space-4)" : "var(--space-5)",
        }}
      >
        <Metric label="Asking" value={usd(row.asking_price)} />
        <Metric label="Current Monthly Rev." value={usd(row.current_monthly_revenue)} />
        <Metric label="ZIP" value={row.zip || "—"} />
        <div className="input-group">
          <label
            htmlFor={`lead-status-${row.id}`}
            className="input-label"
          >
            Status
          </label>
          <select
            id={`lead-status-${row.id}`}
            value={row.status}
            onChange={(e) => onStatusChange(e.target.value)}
            disabled={busy}
            className="input select"
            style={{
              fontWeight: "var(--weight-bold)",
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.6 : 1,
            }}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Notes preview ────────────────────────────────────── */}
      {notesPreview && (
        <div
          style={{
            background: "var(--color-navy)",
            border: "1px solid var(--color-navy-border)",
            borderRadius: "var(--radius-md)",
            padding: "var(--space-3) var(--space-4)",
            color: "var(--color-white-dim)",
            fontSize: "var(--text-base)",
            lineHeight: "var(--leading-normal)",
            marginBottom: "var(--space-5)",
            whiteSpace: "pre-wrap",
          }}
        >
          {notesPreview}{(row.notes || "").length > notesPreview.length ? "…" : ""}
        </div>
      )}

      {/* ── Saved Analyses (DEF-015) ─────────────────────────── */}
      <div style={{ marginBottom: "var(--space-5)" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-2)",
            marginBottom: "var(--space-3)",
          }}
        >
          <SectionLabel>Saved Analyses</SectionLabel>
          <span
            className="badge"
            style={{
              background: "var(--color-navy-lt)",
              color: "var(--color-steel-lt)",
              border: "1px solid var(--color-navy-border)",
            }}
          >
            {savedAnalyses.length}
          </span>
        </div>
        {savedAnalyses.length === 0 ? (
          <div
            style={{
              color: "var(--color-muted)",
              fontSize: "var(--text-sm)",
              fontStyle: "italic",
            }}
          >
            No analyses saved yet
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            {savedAnalyses.map((a) => {
              const dscrNum = Number(a.dscr);
              const dscrLabel = Number.isFinite(dscrNum) ? dscrNum.toFixed(2) : "—";
              return (
                <div
                  key={a.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-3)",
                    flexWrap: "wrap",
                    padding: "var(--space-2) var(--space-3)",
                    background: "var(--color-navy)",
                    border: "1px solid var(--color-navy-border)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "var(--text-sm)",
                    color: "var(--color-white-dim)",
                  }}
                >
                  <span style={{ color: "var(--color-steel-lt)" }}>
                    {shortDate(a.created_at)}
                  </span>
                  <span className={`badge ${recommendationBadgeClass(a.recommendation)}`}>
                    {a.recommendation || "—"}
                  </span>
                  <span>DSCR {dscrLabel}</span>
                  <span>Grade {a.deal_score || "—"}</span>
                  <Link
                    to="/my-analyses"
                    style={{
                      marginLeft: "auto",
                      color: "var(--color-amber)",
                      textDecoration: "none",
                      fontWeight: "var(--weight-semibold)",
                    }}
                  >
                    View →
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Actions ──────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <button type="button" onClick={onAnalyze} className="btn btn-primary">
          Run Analysis
        </button>
        {onEdit && (
          <button type="button" onClick={onEdit} className="btn btn-secondary">
            Edit
          </button>
        )}
        {row.entry_type === "lease_leverage" && (
          <button type="button" onClick={onScenarios} className="btn btn-secondary">
            Scenarios
          </button>
        )}
        {row.entry_type === "lease_leverage" && (
          <button type="button" onClick={onImprovements} className="btn btn-secondary">
            Improvements
          </button>
        )}
        <button
          type="button"
          onClick={onDelete}
          disabled={busy}
          className="btn btn-danger"
          style={{ marginLeft: "auto" }}
        >
          {busy ? "Working…" : "Delete"}
        </button>
      </div>
    </article>
  );
}

// =========================
// METRIC — small labeled value (uses .card-stat-* tokens)
// =========================
function Metric({ label, value }) {
  return (
    <div className="card-stat" style={{ padding: "var(--space-3) var(--space-4)" }}>
      <div className="card-stat-label">{label}</div>
      <div
        className="card-stat-value"
        style={{
          fontSize: "var(--text-xl)",
          color: "var(--color-white)",
        }}
      >
        {value}
      </div>
    </div>
  );
}

// =========================
// EMPTY STATE
// =========================
function EmptyState({ onNew }) {
  return (
    <div
      className="card"
      style={{
        textAlign: "center",
        padding: "var(--space-12) var(--space-6)",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "var(--text-2xl)",
          fontWeight: "var(--weight-bold)",
          color: "var(--color-white)",
          marginBottom: "var(--space-2)",
        }}
      >
        Your pipeline is empty
      </div>
      <p
        style={{
          fontSize: "var(--text-base)",
          lineHeight: "var(--leading-normal)",
          color: "var(--color-steel-lt)",
          maxWidth: 540,
          margin: "0 auto",
          marginBottom: "var(--space-6)",
        }}
      >
        Track prospective deals from sourcing to decision. Click{" "}
        <strong style={{ color: "var(--color-amber)" }}>+ New Lead</strong>{" "}
        to add your first one — drop in an address (or just a name), pick an
        entry type, and you'll be one click away from running it through the
        Deal Analyzer.
      </p>
      <button type="button" onClick={onNew} className="btn btn-primary btn-lg">
        + Add your first lead
      </button>
    </div>
  );
}

// =========================
// NEW LEAD MODAL
// =========================
// Address autocomplete is the primary path. Manual lat/lng with
// reverse-geocode-on-blur is the fallback for off-market parcels or
// land without a clean address — mirrors the v0.9.1 pattern in
// DealAnalyzer.
//
// Test contracts preserved (LeadModal.test.jsx, LeadName.test.jsx):
//   - entry_type <select> is the FIRST combobox in DOM order
//   - Lead Name <input> has placeholder matching /auto-fills from address/i
//   - "Save lead" button accessible by name /save lead/i
//   - onCreate payload shape: { lead: {...lead_name, current_monthly_revenue...}, lease_terms: {...}|null }
export function NewLeadModal({ onClose, onCreate, onUpdate, mode = "create", initialValues = null }) {
  // Edit mode pre-populates from an existing lead row. The 4 patchable fields
  // (lead_name, current_monthly_revenue, asking_price, notes) are editable;
  // address/lat/lng/entry_type are rendered read-only because update-lead.js
  // doesn't accept changes to them today. See DEF-005 fix scope.
  const isEdit = mode === "edit" && !!initialValues;

  const [form, setForm] = useState(() => {
    const base = {
      entry_type:   "lease_leverage",
      address:      "",
      city:         "",
      state:        "",
      zip:          "",
      lat:          "",
      lng:          "",
      place_id:     "",
      asking_price: "",
      notes:        "",
      // Lease structure fields — only sent when entry_type === 'lease_leverage'.
      // Seeded with Forgotten Coast deal defaults so the canonical first deal
      // is one click away.
      ...LEASE_DEFAULTS,
    };
    if (!isEdit) return base;
    return {
      ...base,
      entry_type:   initialValues.entry_type   || base.entry_type,
      address:      initialValues.address      || "",
      city:         initialValues.city         || "",
      state:        initialValues.state        || "",
      zip:          initialValues.zip          || "",
      lat:          initialValues.lat ?? "",
      lng:          initialValues.lng ?? "",
      place_id:     initialValues.place_id     || "",
      asking_price: initialValues.asking_price ?? "",
      notes:        initialValues.notes        || "",
    };
  });
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState("");
  // Same de-dupe guard DealAnalyzer uses so onBlur doesn't fire two
  // reverse-geocode requests for the same lat/lng pair.
  const [lastGeocoded, setLastGeocoded] = useState("");

  // Lead name auto-tracks the address until the user types in the
  // Lead Name field. After that, it sticks to whatever they typed —
  // until the address is cleared, at which point we re-arm the
  // auto-fill so the next address selection seeds the name again.
  // In edit mode we lock auto-fill immediately so the existing lead_name
  // isn't clobbered by the pre-populated address.
  const [leadName, setLeadName] = useState(isEdit ? (initialValues.lead_name || "") : "");
  const userEditedName = useRef(isEdit);

  useEffect(() => {
    if (!form.address) {
      userEditedName.current = false;
      return;
    }
    if (!userEditedName.current) {
      setLeadName(form.address);
    }
  }, [form.address]);

  function handleLeadNameChange(e) {
    userEditedName.current = true;
    setLeadName(e.target.value);
  }

  // Optional. Captured as a string so the input can stay controlled with
  // an empty value; coerced to a parseFloat (or null) only at submit time.
  const [currentMonthlyRevenue, setCurrentMonthlyRevenue] = useState(
    isEdit && initialValues.current_monthly_revenue != null
      ? String(initialValues.current_monthly_revenue)
      : ""
  );

  function handleField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleAddressSelect({ address, lat, lng, placeId, components }) {
    const c = components || {};
    setForm((prev) => ({
      ...prev,
      address,
      city:     c.locality || c.sublocality || c.administrative_area_level_2 || "",
      state:    c.administrative_area_level_1_short || "",
      zip:      c.postal_code || "",
      lat:      Number(lat.toFixed(6)),
      lng:      Number(lng.toFixed(6)),
      place_id: placeId || "",
    }));
  }

  async function handleLatLngBlur() {
    const lat = Number(form.lat);
    const lng = Number(form.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    if (lat === 0 || lng === 0) return;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return;
    if (form.place_id) return;

    const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    if (lastGeocoded === key) return;
    setLastGeocoded(key);

    try {
      const res = await fetch(
        `${API_BASE_URL}/geocode-place?latlng=${encodeURIComponent(key)}`
      );
      const data = await res.json();
      if (!data?.success) return;
      const c = data.components || {};
      setForm((prev) => ({
        ...prev,
        address: prev.address || data.formattedAddress || "",
        city:    prev.city    || c.locality || c.sublocality || c.administrative_area_level_2 || "",
        state:   prev.state   || c.administrative_area_level_1_short || "",
        zip:     prev.zip     || c.postal_code || "",
      }));
    } catch {
      // Stay silent on network failure — manual values stand.
    }
  }

  // Coerce a form string-or-blank to numeric-or-null without surprises.
  // We don't validate that the result is finite — the server-side function
  // does that and returns a 400 with a specific field message if anything
  // is wrong, which the modal surfaces via setModalError.
  function num(v) {
    return v === "" || v === null || v === undefined ? null : Number(v);
  }

  function buildLeaseTermsPayload() {
    // The server requires base_rent_monthly + lease_term_months; everything
    // else is optional and defaults server-side. We send everything the user
    // can see in the modal so server-side validation is the single source
    // of truth for what's valid.
    return {
      base_rent_monthly:                   num(form.base_rent_monthly),
      lease_term_months:                   num(form.lease_term_months),
      renewal_options_count:               num(form.renewal_options_count),
      renewal_option_length_months:        num(form.renewal_option_length_months),
      renewal_notice_days:                 num(form.renewal_notice_days),
      escalation_pct_cap:                  num(form.escalation_pct_cap),
      escalation_index:                    form.escalation_index,
      late_charge_pct:                     num(form.late_charge_pct),
      grace_period_days:                   num(form.grace_period_days),
      sublet_allowed:                      Boolean(form.sublet_allowed),
      utilities_paid_by:                   form.utilities_paid_by,
      option_window_start_month:           num(form.option_window_start_month),
      option_window_end_month:             num(form.option_window_end_month),
      option_price_fixed:                  form.option_price_appraised_at_effective ? null : num(form.option_price_fixed),
      option_price_appraised_at_effective: Boolean(form.option_price_appraised_at_effective),
      rofr_match_days:                     num(form.rofr_match_days),
      improvement_valuation_method:        form.improvement_valuation_method,
      early_term_noi_multiplier_months:    num(form.early_term_noi_multiplier_months),
      monthly_expense_lease_payment:       num(form.base_rent_monthly), // server defaults to base_rent if omitted; sending explicitly for clarity
      monthly_expense_utilities:           num(form.monthly_expense_utilities),
      monthly_expense_maintenance:         num(form.monthly_expense_maintenance),
      monthly_expense_vacancy_reserve:     num(form.monthly_expense_vacancy_reserve),
      monthly_expense_insurance:           num(form.monthly_expense_insurance),
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setModalError("");

    // Edit mode — patch only the 4 server-supported fields via updateLead.
    // Address / lat / lng / entry_type are read-only in the UI and not
    // sent. See DEF-005 — small fix scope.
    if (isEdit) {
      setSubmitting(true);
      const patch = {
        id:                      initialValues.id,
        lead_name:               leadName.trim() || null,
        current_monthly_revenue: currentMonthlyRevenue ? parseFloat(currentMonthlyRevenue) : null,
        asking_price:            form.asking_price === "" ? null : Number(form.asking_price),
        notes:                   form.notes.trim() || null,
      };
      const { error } = await onUpdate(patch);
      setSubmitting(false);
      if (error) {
        setModalError(error.message || "Could not update the lead.");
        return;
      }
      onClose();
      return;
    }

    if (!form.entry_type) {
      setModalError("Entry type is required.");
      return;
    }

    const isLeaseLeverage = form.entry_type === "lease_leverage";

    // Client-side guard against the two required lease_terms fields so we
    // catch obvious mistakes before creating the lead and triggering a
    // rollback round-trip. The server enforces these too — this is just
    // a friendlier first line of defense.
    if (isLeaseLeverage) {
      if (form.base_rent_monthly === "" || !Number.isFinite(Number(form.base_rent_monthly))) {
        setModalError("Base rent (monthly) is required for a lease-leverage lead.");
        return;
      }
      if (form.lease_term_months === "" || !(Number(form.lease_term_months) > 0)) {
        setModalError("Lease term (months) must be a positive number for a lease-leverage lead.");
        return;
      }
    }

    setSubmitting(true);
    const leadInput = {
      entry_type:              form.entry_type,
      lead_name:               leadName.trim() || null,
      current_monthly_revenue: currentMonthlyRevenue ? parseFloat(currentMonthlyRevenue) : null,
      address:                 form.address.trim() || null,
      city:                    form.city.trim()    || null,
      state:                   form.state.trim()   || null,
      zip:                     form.zip.trim()     || null,
      lat:                     form.lat === "" ? null : Number(form.lat),
      lng:                     form.lng === "" ? null : Number(form.lng),
      asking_price:            form.asking_price === "" ? null : Number(form.asking_price),
      notes:                   form.notes.trim() || null,
    };
    const leaseTermsInput = isLeaseLeverage ? buildLeaseTermsPayload() : null;

    const { error } = await onCreate({ lead: leadInput, lease_terms: leaseTermsInput });
    setSubmitting(false);
    if (error) {
      setModalError(error.message || "Could not create the lead.");
      return;
    }
    onClose();
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        // --color-navy at 85% alpha — design system has no dedicated backdrop token.
        background: "color-mix(in srgb, var(--color-navy) 85%, transparent)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "var(--space-8) var(--space-4)",
        zIndex: 9999,
        overflowY: "auto",
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="card-elevated"
        style={{
          width: "100%",
          maxWidth: 640,
          padding: "var(--space-7, var(--space-6))",
          color: "var(--color-white)",
          fontFamily: "var(--font-body)",
        }}
      >
        {/* ── Modal header ───────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "var(--space-3)",
            marginBottom: "var(--space-5)",
          }}
        >
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "var(--text-2xl)",
              fontWeight: "var(--weight-black)",
              letterSpacing: "var(--tracking-tight)",
              textTransform: "uppercase",
              color: "var(--color-white)",
              margin: 0,
            }}
          >
            {isEdit ? "Edit Lead" : "New Lead"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              border: "none",
              color: "var(--color-steel)",
              fontSize: "var(--text-2xl)",
              cursor: "pointer",
              lineHeight: 1,
              padding: "var(--space-1) var(--space-2)",
            }}
          >
            ×
          </button>
        </div>

        {modalError && (
          <div
            role="alert"
            style={{
              border: "1px solid var(--color-danger)",
              background: "var(--color-danger-ghost)",
              color: "var(--color-danger-lt)",
              padding: "var(--space-3) var(--space-4)",
              borderRadius: "var(--radius-md)",
              marginBottom: "var(--space-4)",
              fontSize: "var(--text-base)",
              fontWeight: "var(--weight-semibold)",
            }}
          >
            {modalError}
          </div>
        )}

        {isEdit && (
          <div
            role="note"
            style={{
              padding: "var(--space-3) var(--space-4)",
              borderRadius: "var(--radius-md)",
              background: "var(--color-navy)",
              border: "1px solid var(--color-navy-border)",
              color: "var(--color-steel-lt)",
              fontSize: "var(--text-sm)",
              lineHeight: "var(--leading-normal)",
              marginBottom: "var(--space-4)",
            }}
          >
            Address and entry type cannot be changed after creation — delete this
            lead and add a new one.
          </div>
        )}

        {/* ── Entry type — MUST be first combobox in DOM (test contract) ── */}
        <div className="input-group" style={{ marginBottom: "var(--space-4)" }}>
          <label className="input-label" htmlFor="lead-entry-type">Entry type{isEdit ? "" : " *"}</label>
          <select
            id="lead-entry-type"
            value={form.entry_type}
            onChange={(e) => handleField("entry_type", e.target.value)}
            className="input select"
            required
            disabled={isEdit}
          >
            {ENTRY_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* ── Lead name — placeholder is a test contract ───── */}
        <div className="input-group" style={{ marginBottom: "var(--space-4)" }}>
          <label className="input-label" htmlFor="lead-name">Lead name</label>
          <input
            id="lead-name"
            className="input"
            value={leadName}
            onChange={handleLeadNameChange}
            placeholder="Auto-fills from address — edit to override"
          />
        </div>

        <div className="input-group" style={{ marginBottom: "var(--space-4)" }}>
          <label className="input-label" htmlFor="lead-current-rev">
            Current monthly revenue (if occupied)
          </label>
          <input
            id="lead-current-rev"
            className="input"
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={currentMonthlyRevenue}
            onChange={(e) => setCurrentMonthlyRevenue(e.target.value)}
            placeholder="6800.00"
          />
        </div>

        <div className="input-group" style={{ marginBottom: "var(--space-4)" }}>
          <label className="input-label" htmlFor={isEdit ? "lead-address-readonly" : undefined}>
            Address
          </label>
          {isEdit ? (
            <input
              id="lead-address-readonly"
              className="input"
              value={form.address || "—"}
              disabled
              readOnly
            />
          ) : (
            <>
              <AddressAutocomplete
                initialValue={form.address}
                placeholder="Search address, RV park, or place name…"
                onSelect={handleAddressSelect}
              />
              <span className="input-hint" style={{ marginTop: "var(--space-1)" }}>
                Pick a result to auto-fill city, state, ZIP, and lat/lng.
              </span>
            </>
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: "var(--space-3)",
            marginBottom: "var(--space-4)",
          }}
        >
          <div className="input-group">
            <label className="input-label" htmlFor="lead-city">City</label>
            <input
              id="lead-city"
              className="input"
              value={form.city}
              onChange={(e) => handleField("city", e.target.value)}
              disabled={isEdit}
            />
          </div>
          <div className="input-group">
            <label className="input-label" htmlFor="lead-state">State</label>
            <input
              id="lead-state"
              className="input"
              value={form.state}
              onChange={(e) => handleField("state", e.target.value)}
              maxLength={2}
              placeholder="TX"
              disabled={isEdit}
            />
          </div>
          <div className="input-group">
            <label className="input-label" htmlFor="lead-zip">ZIP</label>
            <input
              id="lead-zip"
              className="input"
              value={form.zip}
              onChange={(e) => handleField("zip", e.target.value)}
              disabled={isEdit}
            />
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: "var(--space-3)",
            marginBottom: "var(--space-1)",
          }}
        >
          <div className="input-group">
            <label className="input-label" htmlFor="lead-lat">Latitude</label>
            <input
              id="lead-lat"
              className="input"
              value={form.lat}
              onChange={(e) => handleField("lat", e.target.value)}
              onBlur={handleLatLngBlur}
              inputMode="decimal"
              placeholder="32.7767"
              disabled={isEdit}
            />
          </div>
          <div className="input-group">
            <label className="input-label" htmlFor="lead-lng">Longitude</label>
            <input
              id="lead-lng"
              className="input"
              value={form.lng}
              onChange={(e) => handleField("lng", e.target.value)}
              onBlur={handleLatLngBlur}
              inputMode="decimal"
              placeholder="-96.7970"
              disabled={isEdit}
            />
          </div>
        </div>
        {!isEdit && (
          <span className="input-hint" style={{ display: "block", marginBottom: "var(--space-4)" }}>
            No address handy? Enter lat/lng and we'll fill in the rest.
          </span>
        )}

        <div className="input-group" style={{ marginBottom: "var(--space-4)" }}>
          <label className="input-label" htmlFor="lead-asking">
            {form.entry_type === "lease_leverage" ? "Purchase option price" : "Asking price"}
          </label>
          <input
            id="lead-asking"
            className="input"
            value={form.asking_price}
            onChange={(e) => handleField("asking_price", e.target.value)}
            inputMode="numeric"
            placeholder="425000"
          />
          {form.entry_type === "lease_leverage" && (
            <span className="input-hint" style={{ marginTop: "var(--space-1)" }}>
              The pipeline-level option strike. You'll set the detailed option price + window in
              the Lease structure section below.
            </span>
          )}
        </div>

        <div className="input-group" style={{ marginBottom: "var(--space-5)" }}>
          <label className="input-label" htmlFor="lead-notes">Notes</label>
          <textarea
            id="lead-notes"
            className="input"
            style={{ minHeight: 90, resize: "vertical", fontFamily: "inherit" }}
            value={form.notes}
            onChange={(e) => handleField("notes", e.target.value)}
            placeholder="Source, contact, why this caught your eye…"
          />
        </div>

        {!isEdit && form.entry_type === "lease_leverage" && (
          <LeaseStructureSection form={form} handleField={handleField} />
        )}

        <div
          style={{
            display: "flex",
            gap: "var(--space-3)",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            marginTop: "var(--space-5)",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Save lead"}
          </button>
        </div>
      </form>
    </div>
  );
}

// =========================
// LEASE STRUCTURE SECTION
// Renders inside NewLeadModal only when entry_type === 'lease_leverage'.
// Pure form — state lives on the parent form; this component is just
// presentational. Field defaults come from LEASE_DEFAULTS (Forgotten
// Coast deal) so the canonical first deal is one click away.
// =========================
function LeaseStructureSection({ form, handleField }) {
  const grid2 = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "var(--space-3)",
  };
  const grid3 = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: "var(--space-3)",
  };

  return (
    <section
      style={{
        border: "1px solid var(--color-navy-border)",
        borderRadius: "var(--radius-md)",
        padding: "var(--space-5)",
        background: "var(--color-navy)",
        marginBottom: "var(--space-5)",
      }}
    >
      <h3
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "var(--text-lg)",
          fontWeight: "var(--weight-bold)",
          color: "var(--color-white)",
          margin: 0,
          marginBottom: "var(--space-1)",
        }}
      >
        Lease structure
      </h3>
      <p
        style={{
          fontSize: "var(--text-sm)",
          color: "var(--color-steel)",
          margin: 0,
          marginBottom: "var(--space-4)",
        }}
      >
        Defaults below match the Forgotten Coast deal. Override anything that doesn't fit.
      </p>

      {/* Rent & term */}
      <SectionLabel style={{ marginTop: "var(--space-4)", marginBottom: "var(--space-2)" }}>
        Rent &amp; term
      </SectionLabel>
      <div style={grid2}>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-base-rent">Base rent / month *</label>
          <input
            id="ls-base-rent"
            className="input"
            value={form.base_rent_monthly}
            onChange={(e) => handleField("base_rent_monthly", e.target.value)}
            inputMode="numeric"
            placeholder="1800"
          />
        </div>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-term">Lease term (months) *</label>
          <input
            id="ls-term"
            className="input"
            value={form.lease_term_months}
            onChange={(e) => handleField("lease_term_months", e.target.value)}
            inputMode="numeric"
            placeholder="60"
          />
        </div>
      </div>

      {/* Renewal options */}
      <SectionLabel style={{ marginTop: "var(--space-4)", marginBottom: "var(--space-2)" }}>
        Renewal options
      </SectionLabel>
      <div style={grid3}>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-renewal-count">Count</label>
          <input
            id="ls-renewal-count"
            className="input"
            value={form.renewal_options_count}
            onChange={(e) => handleField("renewal_options_count", e.target.value)}
            inputMode="numeric"
          />
        </div>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-renewal-len">Length (months)</label>
          <input
            id="ls-renewal-len"
            className="input"
            value={form.renewal_option_length_months}
            onChange={(e) => handleField("renewal_option_length_months", e.target.value)}
            inputMode="numeric"
          />
        </div>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-renewal-notice">Notice (days)</label>
          <input
            id="ls-renewal-notice"
            className="input"
            value={form.renewal_notice_days}
            onChange={(e) => handleField("renewal_notice_days", e.target.value)}
            inputMode="numeric"
          />
        </div>
      </div>

      {/* Escalation + late charge */}
      <SectionLabel style={{ marginTop: "var(--space-4)", marginBottom: "var(--space-2)" }}>
        Escalation &amp; late charge
      </SectionLabel>
      <div style={grid2}>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-esc-idx">Escalation index</label>
          <select
            id="ls-esc-idx"
            className="input select"
            value={form.escalation_index}
            onChange={(e) => handleField("escalation_index", e.target.value)}
          >
            {ESCALATION_INDEX_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-esc-cap">Escalation cap (%)</label>
          <input
            id="ls-esc-cap"
            className="input"
            value={form.escalation_pct_cap}
            onChange={(e) => handleField("escalation_pct_cap", e.target.value)}
            inputMode="decimal"
            placeholder="2.0"
          />
        </div>
      </div>
      <div style={{ ...grid2, marginTop: "var(--space-3)" }}>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-late-pct">Late charge (%)</label>
          <input
            id="ls-late-pct"
            className="input"
            value={form.late_charge_pct}
            onChange={(e) => handleField("late_charge_pct", e.target.value)}
            inputMode="decimal"
            placeholder="5.0"
          />
        </div>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-grace">Grace period (days)</label>
          <input
            id="ls-grace"
            className="input"
            value={form.grace_period_days}
            onChange={(e) => handleField("grace_period_days", e.target.value)}
            inputMode="numeric"
          />
        </div>
      </div>

      {/* Sublet + utilities */}
      <SectionLabel style={{ marginTop: "var(--space-4)", marginBottom: "var(--space-2)" }}>
        Sublet &amp; utilities
      </SectionLabel>
      <div style={grid2}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-2)",
            paddingTop: "var(--space-5)",
            color: "var(--color-white-dim)",
          }}
        >
          <input
            type="checkbox"
            id="sublet_allowed"
            checked={Boolean(form.sublet_allowed)}
            onChange={(e) => handleField("sublet_allowed", e.target.checked)}
          />
          <label
            htmlFor="sublet_allowed"
            style={{ fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)" }}
          >
            Sublet allowed
          </label>
        </div>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-utilities">Utilities paid by</label>
          <select
            id="ls-utilities"
            className="input select"
            value={form.utilities_paid_by}
            onChange={(e) => handleField("utilities_paid_by", e.target.value)}
          >
            {UTILITIES_PAID_BY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Purchase option */}
      <SectionLabel style={{ marginTop: "var(--space-4)", marginBottom: "var(--space-2)" }}>
        Purchase option
      </SectionLabel>
      <div style={grid3}>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-opt-start">Window start (month)</label>
          <input
            id="ls-opt-start"
            className="input"
            value={form.option_window_start_month}
            onChange={(e) => handleField("option_window_start_month", e.target.value)}
            inputMode="numeric"
            placeholder="24"
          />
        </div>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-opt-end">Window end (month)</label>
          <input
            id="ls-opt-end"
            className="input"
            value={form.option_window_end_month}
            onChange={(e) => handleField("option_window_end_month", e.target.value)}
            inputMode="numeric"
            placeholder="60"
          />
        </div>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-rofr">ROFR match (days)</label>
          <input
            id="ls-rofr"
            className="input"
            value={form.rofr_match_days}
            onChange={(e) => handleField("rofr_match_days", e.target.value)}
            inputMode="numeric"
            placeholder="10"
          />
        </div>
      </div>
      <div
        style={{
          marginTop: "var(--space-3)",
          display: "flex",
          alignItems: "center",
          gap: "var(--space-2)",
          color: "var(--color-white-dim)",
        }}
      >
        <input
          type="checkbox"
          id="option_price_appraised_at_effective"
          checked={Boolean(form.option_price_appraised_at_effective)}
          onChange={(e) => handleField("option_price_appraised_at_effective", e.target.checked)}
        />
        <label
          htmlFor="option_price_appraised_at_effective"
          style={{ fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)" }}
        >
          Price is appraised at effective date (locked in advance)
        </label>
      </div>
      {!form.option_price_appraised_at_effective && (
        <div className="input-group" style={{ marginTop: "var(--space-3)" }}>
          <label className="input-label" htmlFor="ls-opt-fixed">Fixed option price ($)</label>
          <input
            id="ls-opt-fixed"
            className="input"
            value={form.option_price_fixed}
            onChange={(e) => handleField("option_price_fixed", e.target.value)}
            inputMode="numeric"
            placeholder="425000"
          />
        </div>
      )}

      {/* Improvements + early termination */}
      <SectionLabel style={{ marginTop: "var(--space-4)", marginBottom: "var(--space-2)" }}>
        Improvements &amp; early termination
      </SectionLabel>
      <div style={grid2}>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-imp-val">Improvement valuation method</label>
          <select
            id="ls-imp-val"
            className="input select"
            value={form.improvement_valuation_method}
            onChange={(e) => handleField("improvement_valuation_method", e.target.value)}
          >
            {IMPROVEMENT_VALUATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-early-mult">Early-term NOI multiplier (months)</label>
          <input
            id="ls-early-mult"
            className="input"
            value={form.early_term_noi_multiplier_months}
            onChange={(e) => handleField("early_term_noi_multiplier_months", e.target.value)}
            inputMode="numeric"
            placeholder="12"
          />
        </div>
      </div>

      {/* Expense stack */}
      <SectionLabel style={{ marginTop: "var(--space-4)", marginBottom: "var(--space-2)" }}>
        Monthly expense stack
      </SectionLabel>
      <p
        style={{
          fontSize: "var(--text-sm)",
          color: "var(--color-muted)",
          margin: 0,
          marginBottom: "var(--space-2)",
        }}
      >
        Shared across all 3 scenarios. The lease payment line is auto-set to the base rent above on save.
      </p>
      <div style={grid2}>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-exp-util">Utilities</label>
          <input
            id="ls-exp-util"
            className="input"
            value={form.monthly_expense_utilities}
            onChange={(e) => handleField("monthly_expense_utilities", e.target.value)}
            inputMode="numeric"
            placeholder="250"
          />
        </div>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-exp-maint">Maintenance</label>
          <input
            id="ls-exp-maint"
            className="input"
            value={form.monthly_expense_maintenance}
            onChange={(e) => handleField("monthly_expense_maintenance", e.target.value)}
            inputMode="numeric"
            placeholder="150"
          />
        </div>
      </div>
      <div style={{ ...grid2, marginTop: "var(--space-3)" }}>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-exp-vac">Vacancy reserve</label>
          <input
            id="ls-exp-vac"
            className="input"
            value={form.monthly_expense_vacancy_reserve}
            onChange={(e) => handleField("monthly_expense_vacancy_reserve", e.target.value)}
            inputMode="numeric"
            placeholder="200"
          />
        </div>
        <div className="input-group">
          <label className="input-label" htmlFor="ls-exp-ins">Insurance</label>
          <input
            id="ls-exp-ins"
            className="input"
            value={form.monthly_expense_insurance}
            onChange={(e) => handleField("monthly_expense_insurance", e.target.value)}
            inputMode="numeric"
            placeholder="100"
          />
        </div>
      </div>
    </section>
  );
}
